import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Button, Space, Row, Col, Breadcrumb, Modal, message,
    Typography, Select, DatePicker, Input, Tag, Descriptions, Tabs
} from "antd";
import {
    HomeOutlined, SafetyCertificateOutlined, FilterOutlined, ClearOutlined,
    SearchOutlined, CheckOutlined, CloseOutlined
} from "@ant-design/icons";
import api from "../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import "../../styles/fluidDesign.css";
import { APPROVAL_STATUS_COLORS } from "../../constant/expenseOptions";

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;

const tabItems = [
    { key: "pending", label: "Pending Approval" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "cancelled", label: "Cancelled" },
];

const ApproveExpenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const [search, setSearch] = useState("");
    const [filterCat, setFilterCat] = useState(undefined);
    const [filterSupplier, setFilterSupplier] = useState(undefined);
    const [filterDates, setFilterDates] = useState([]);
    const [applied, setApplied] = useState({});

    const [reviewItem, setReviewItem] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);

    const fetchRefData = useCallback(async () => {
        try {
            const [catRes, supRes] = await Promise.all([
                api.get("/masterdropdown/listof_activeexpensecategories.php"),
                api.get("/masterdropdown/listof_activesuppliers.php"),
            ]);
            setCategories(catRes.data.dataset || []);
            setSuppliers(supRes.data.dataset || []);
        } catch {
            message.error("Failed to load reference data.");
        }
    }, []);

    const fetchExpenses = useCallback(async (page = 1, pageSize = 10, tab = activeTab, filters = {}) => {
        setLoading(true);
        try {
            const res = await api.post("/expenses/list_approvals.php", {
                page,
                limit: pageSize,
                tab,
                expcat_id: filters.expcat_id || 0,
                sup_id: filters.sup_id || 0,
                search: filters.search || "",
                start_date: filters.start_date || "",
                end_date: filters.end_date || "",
            });
            setExpenses(res.data.dataset || []);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch expenses.");
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchRefData();
        fetchExpenses(1, 10, activeTab, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            expcat_id: filterCat,
            sup_id: filterSupplier,
            start_date: filterDates?.[0] ? filterDates[0].format("YYYY-MM-DD") : "",
            end_date: filterDates?.[1] ? filterDates[1].format("YYYY-MM-DD") : "",
        };
        setApplied(filters);
        fetchExpenses(1, pagination.pageSize, activeTab, filters);
    };

    const handleClear = () => {
        setSearch("");
        setFilterCat(undefined);
        setFilterSupplier(undefined);
        setFilterDates([]);
        setApplied({});
        fetchExpenses(1, pagination.pageSize, activeTab, {});
    };

    const handleReview = async (action) => {
        setReviewLoading(true);
        try {
            const res = await api.post("/expenses/approve.php", {
                exp_id: reviewItem.exp_id,
                status: action === "approve" ? "Approved" : "Rejected",
            });
            if (res.data.status === "success") {
                message.success(res.data.message);
                setReviewItem(null);
                fetchExpenses(1, pagination.pageSize, activeTab, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update expense.");
        } finally {
            setReviewLoading(false);
        }
    };

    const columns = [
        {
            title: "Expense Ref", dataIndex: "exp_ref", key: "exp_ref",
            render: (v) => <Typography.Text strong>{v}</Typography.Text>,
        },
        {
            title: "Category", dataIndex: "expcat_name", key: "expcat_name",
            render: (v) => v || "-",
        },
        {
            title: "Supplier", dataIndex: "supplier_name", key: "supplier_name",
            render: (v) => v || "-",
        },
        {
            title: "Title", dataIndex: "exp_title", key: "exp_title",
            ellipsis: true,
        },
        {
            title: "Amount", dataIndex: "exp_amount", key: "exp_amount", align: "right",
            render: (v) => <strong>LKR {parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>,
        },
        {
            title: "Req. Date", dataIndex: "payment_req_date", key: "payment_req_date", align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "Status", dataIndex: "approved_status", key: "approved_status", align: "center",
            render: (s) => {
                const color = APPROVAL_STATUS_COLORS[s] || "default";
                return <Tag color={color} style={{ fontWeight: 600 }}>{s}</Tag>;
            },
        },
        {
            title: "Created By", dataIndex: "creator_name", key: "creator_name",
            render: (v) => v || "-",
        },
        {
            title: "Action", key: "action", align: "center", fixed: "right", width: 110,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={record.approved_status === "Pending" ? <CheckOutlined /> : <SearchOutlined />}
                    onClick={() => setReviewItem(record)}
                >
                    {record.approved_status === "Pending" ? "Review" : "View"}
                </Button>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Expenses' },
                                { title: 'Approve Expenses' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <SafetyCertificateOutlined /> Approve Expenses
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Review and approve pending expense requests</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div style={{ padding: "16px 16px 0" }}>
                        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
                    </div>

                    {/* Advanced filter bar */}
                    <div style={{ margin: "0 16px 16px" }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Ref, title, supplier ref"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Category:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterCat}
                                    onChange={setFilterCat}
                                    placeholder="All Categories"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {categories.map((c) => <Option key={c.value} value={Number(c.value)}>{c.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Supplier:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterSupplier}
                                    onChange={setFilterSupplier}
                                    placeholder="All Suppliers"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {suppliers.map((s) => <Option key={s.value} value={Number(s.value)}>{s.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Req. Date Range:</div>
                                <RangePicker value={filterDates} onChange={setFilterDates} style={{ width: "100%" }} />
                            </Col>
                            <Col xs={24} sm={24} lg={6}>
                                <Row gutter={8} className="fluid-filter-actions">
                                    <Col span={12}>
                                        <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary" block>
                                            Apply
                                        </Button>
                                    </Col>
                                    <Col span={12}>
                                        <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary" block>
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table" style={{ padding: "0 16px 16px" }}>
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={expenses}
                            rowKey="exp_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pg) => fetchExpenses(pg.current, pg.pageSize, activeTab, applied)}
                            loading={loading}
                            scroll={{ x: 1100 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Review modal */}
            <Modal
                title={`Review Expense - ${reviewItem?.exp_ref || ""}`}
                open={!!reviewItem}
                onCancel={() => setReviewItem(null)}
                footer={null}
                width={640}
            >
                {reviewItem && (
                    <>
                        <Descriptions column={2} size="small" bordered>
                            <Descriptions.Item label="Reference" span={2}>
                                <Typography.Text strong>{reviewItem.exp_ref}</Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Category">{reviewItem.expcat_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Supplier">{reviewItem.supplier_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Title" span={2}>{reviewItem.exp_title}</Descriptions.Item>
                            <Descriptions.Item label="Description" span={2}>{reviewItem.exp_desc || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Amount" span={2}>
                                <Typography.Text strong style={{ color: "#1677ff" }}>
                                    LKR {parseFloat(reviewItem.exp_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Supplier Ref">{reviewItem.supplier_ref || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Payment Req. Date">{dayjs(reviewItem.payment_req_date).format("YYYY-MM-DD")}</Descriptions.Item>
                            <Descriptions.Item label="Created By">{reviewItem.creator_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Created At">{dayjs(reviewItem.cr_dt).format("YYYY-MM-DD HH:mm")}</Descriptions.Item>
                            {reviewItem.approved_status !== "Pending" && (
                                <>
                                    <Descriptions.Item label="Approved By">{reviewItem.approver_name || "-"}</Descriptions.Item>
                                    <Descriptions.Item label="Approved At">
                                        {reviewItem.approved_dt ? dayjs.unix(Number(reviewItem.approved_dt)).format("YYYY-MM-DD HH:mm") : "-"}
                                    </Descriptions.Item>
                                </>
                            )}
                        </Descriptions>
                        {reviewItem.approved_status === "Pending" && (
                            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                <Button danger icon={<CloseOutlined />} loading={reviewLoading} onClick={() => handleReview("reject")}>
                                    Reject
                                </Button>
                                <Button type="primary" icon={<CheckOutlined />} loading={reviewLoading} onClick={() => handleReview("approve")}>
                                    Approve
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Modal>
        </Layout>
    );
};

export default ApproveExpenses;
